import { XIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/class-name";

export interface ModalLayoutProps {
    /** Title content; rendered with standard modal title styles when `header` is not set. */
    title?: React.ReactNode;
    /** Custom header content (left side, before close). Takes precedence over `title`. */
    header?: React.ReactNode;
    /** Optional icon or node shown to the left of `header` or the `title` typography. */
    headerLeftAdornment?: React.ReactNode;
    onClickClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

export function ModalLayout({
    title,
    header,
    headerLeftAdornment,
    onClickClose,
    children,
    className,
    style,
}: ModalLayoutProps) {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    // Guards against fractional scrollHeight rounding causing a false overflow signal.
    const SUB_PIXEL_TOLERANCE = 1;

    const checkOverflow = React.useCallback(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }
        const { scrollHeight, clientHeight } = el;
        setIsOverflowing(scrollHeight > clientHeight + SUB_PIXEL_TOLERANCE);
    }, []);

    React.useEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return () => {};
        }
        checkOverflow();

        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(el);
            return () => resizeObserver.disconnect();
        }

        // The window listener below is a fallback for environments without ResizeObserver.
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [checkOverflow]);

    const headerTitleContent =
        header ?? (title ? <h2 className={"font-heading font-semibold text-xl leading-none"}>{title}</h2> : null);
    const hasHeaderLeft = Boolean(headerLeftAdornment ?? headerTitleContent);
    const showHeaderRow = Boolean(onClickClose ?? hasHeaderLeft);

    return (
        <div className={cn("flex min-h-0 min-w-0 flex-1 flex-col p-0", className)} style={style}>
            {showHeaderRow && (
                <div
                    className={cn(
                        "flex shrink-0 flex-row items-center px-6 py-[18px]",
                        hasHeaderLeft ? "justify-between" : "justify-end",
                    )}
                >
                    {hasHeaderLeft && (
                        <div className={"flex min-w-0 flex-1 flex-row items-center gap-2 pr-2"}>
                            {headerLeftAdornment}
                            {headerTitleContent}
                        </div>
                    )}
                    {Boolean(onClickClose) && (
                        <Button
                            aria-label={"Close modal"}
                            className={"shrink-0"}
                            onClick={onClickClose}
                            size={"icon-lg"}
                            type={"button"}
                            variant={"ghost"}
                        >
                            <XIcon className={"size-6"} />
                        </Button>
                    )}
                </div>
            )}
            <div
                className={cn(
                    "flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-6 pb-6 gap-6",
                    showHeaderRow && isOverflowing && "border-t border-border mb-6",
                )}
                ref={contentRef}
            >
                {children}
            </div>
        </div>
    );
}
